import React, { useState } from 'react';
import { ValidationResponse, LeadImportData } from '../../services/leadImportService';

interface ImportPreviewProps {
  validationResults: ValidationResponse;
  onImport: () => void;
  onBack: () => void;
  loading: boolean;
  skipDuplicates: boolean;
  onSkipDuplicatesChange: (skip: boolean) => void;
}

const ImportPreview: React.FC<ImportPreviewProps> = ({
  validationResults,
  onImport,
  onBack,
  loading,
  skipDuplicates,
  onSkipDuplicatesChange
}) => {
  const [selectedTab, setSelectedTab] = useState<'valid' | 'invalid' | 'duplicates' | 'warnings'>('valid');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const { validation, summary } = validationResults;

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const renderLeadCard = (lead: LeadImportData, lineNumber?: number, expanded: boolean = false, onToggle?: () => void) => (
    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-medium text-gray-900">{lead.parent_name}</h4>
            {lineNumber && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                Line {lineNumber}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600 mb-2">{lead.email}</div>

          {/* Basic Info Row */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-2">
            {lead.phone && <span>📞 {lead.phone}</span>}
            {lead.timeline && <span>⏰ {lead.timeline}</span>}
            <span>👶 {lead.children?.length || 0} children</span>
          </div>

          {/* Children Summary */}
          {!expanded && lead.children && lead.children.length > 0 && (
            <div className="text-sm text-gray-600">
              {lead.children.map((child, index) => (
                <span key={index} className="mr-3">
                  {child.name}
                  {child.special_needs && child.special_needs.length > 0 && (
                    <span className="ml-1 text-purple-600 text-xs">
                      [{child.special_needs.slice(0, 2).join(', ')}{child.special_needs.length > 2 ? '...' : ''}]
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Expanded Details */}
          {expanded && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
              {lead.timeline_date && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Specific Start Date:</span>
                  <span className="ml-2 text-gray-600">{new Date(lead.timeline_date).toLocaleDateString()}</span>
                </div>
              )}

              {lead.biggest_concerns && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Concerns:</span>
                  <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded text-xs">
                    {lead.biggest_concerns}
                  </div>
                </div>
              )}

              {lead.children && lead.children.length > 0 && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Children Details:</span>
                  <div className="mt-1 space-y-2">
                    {lead.children.map((child, childIndex) => (
                      <div key={childIndex} className="bg-gray-50 p-3 rounded">
                        <div className="font-medium text-gray-800 mb-1">
                          {child.name}
                          {child.age && <span className="ml-2 text-gray-600">({child.age} years)</span>}
                          {child.grade && <span className="ml-2 text-gray-600">- {child.grade}</span>}
                        </div>

                        {child.special_needs && child.special_needs.length > 0 && (
                          <div className="mb-1">
                            <span className="text-xs font-medium text-gray-700">Special Needs:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {child.special_needs.map((need, needIndex) => (
                                <span key={needIndex} className="inline-flex px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                                  {need}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {child.current_support && child.current_support.length > 0 && (
                          <div>
                            <span className="text-xs font-medium text-gray-700">Current Support:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {child.current_support.map((support, supportIndex) => (
                                <span key={supportIndex} className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                                  {support}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {onToggle && (
          <button
            onClick={onToggle}
            className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {expanded ? '▲' : '▼'}
          </button>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'valid':
        return (
          <div className="space-y-3">
            {validation.valid.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No valid leads found
              </div>
            ) : (
              validation.valid.map((item, index) => (
                <div key={index}>
                  {renderLeadCard(
                    item.lead,
                    item.lineNumber,
                    expandedItems.has(index),
                    () => toggleExpanded(index)
                  )}
                </div>
              ))
            )}
          </div>
        );

      case 'invalid':
        return (
          <div className="space-y-3">
            {validation.invalid.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No invalid leads
              </div>
            ) : (
              validation.invalid.map((item, index) => (
                <div key={index} className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-medium text-red-900">Line {item.lineNumber}</span>
                      <span className="ml-2 text-red-700">{item.lead.email}</span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-sm text-red-800 font-medium mb-1">Errors:</div>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                      {item.errors.map((error, errorIndex) => (
                        <li key={errorIndex}>{error}</li>
                      ))}
                    </ul>
                  </div>
                  {renderLeadCard(item.lead)}
                </div>
              ))
            )}
          </div>
        );

      case 'duplicates':
        return (
          <div className="space-y-3">
            {validation.duplicates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No duplicate emails found
              </div>
            ) : (
              validation.duplicates.map((item, index) => (
                <div key={index} className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium text-yellow-900">Line {item.lineNumber}</span>
                      <span className="ml-2 text-yellow-700">{item.email}</span>
                    </div>
                    <span className="text-sm text-yellow-600">{item.reason}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      case 'warnings':
        return (
          <div className="space-y-3">
            {validation.warnings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No warnings
              </div>
            ) : (
              validation.warnings.map((item, index) => (
                <div key={index} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                  <div className="mb-2">
                    <span className="font-medium text-orange-900">Line {item.lineNumber}</span>
                    <span className="ml-2 text-orange-700">{item.lead.email}</span>
                  </div>
                  <div className="mb-3">
                    <div className="text-sm text-orange-800 font-medium mb-1">Warnings:</div>
                    <ul className="list-disc list-inside text-sm text-orange-700 space-y-1">
                      {item.warnings.map((warning, warningIndex) => (
                        <li key={warningIndex}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                  {renderLeadCard(item.lead)}
                </div>
              ))
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Preview Import Data</h3>
        <p className="text-gray-600">
          Review the leads that will be imported and configure import settings
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{summary.valid}</div>
          <div className="text-sm text-blue-800">Ready to Import</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{summary.invalid}</div>
          <div className="text-sm text-red-800">Invalid</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{summary.duplicates}</div>
          <div className="text-sm text-yellow-800">Duplicates</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{summary.warnings}</div>
          <div className="text-sm text-orange-800">Warnings</div>
        </div>
      </div>

      {/* Import Settings */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Import Settings</h4>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={skipDuplicates}
              onChange={(e) => onSkipDuplicatesChange(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Skip duplicate emails (recommended)
            </span>
          </label>
          <p className="text-xs text-gray-500 ml-6">
            Duplicate emails will be skipped to prevent creating multiple leads for the same person
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'valid', label: 'Valid Leads', count: summary.valid, color: 'blue' },
            { id: 'invalid', label: 'Invalid', count: summary.invalid, color: 'red' },
            { id: 'duplicates', label: 'Duplicates', count: summary.duplicates, color: 'yellow' },
            { id: 'warnings', label: 'Warnings', count: summary.warnings, color: 'orange' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === tab.id
                  ? `border-${tab.color}-500 text-${tab.color}-600`
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  tab.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                  tab.color === 'red' ? 'bg-red-100 text-red-600' :
                  tab.color === 'yellow' ? 'bg-yellow-100 text-yellow-600' :
                  'bg-orange-100 text-orange-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="max-h-96 overflow-y-auto">
        {renderTabContent()}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4 pt-4 border-t border-gray-200">
        <button
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={loading}
        >
          ← Back to Validation
        </button>
        <button
          onClick={onImport}
          disabled={loading || summary.valid === 0}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Importing...' : `🚀 Import ${summary.valid} Leads`}
        </button>
      </div>
    </div>
  );
};

export default ImportPreview;